// Google Ads (gtag.js) loader, gated on explicit consent.
//
// Tag: AW-980858762 (Google Ads conversion). Nothing fires until the user
// clicks Accept in the cookie banner. Decline (or no choice) → no script
// loaded, no cookies set, no requests to Google.

const GTAG_ID = 'AW-18123061994';
const GTAG_SRC = `https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`;
const CONSENT_KEY = 'yd-consent'; // 'granted' | 'denied'

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export type ConsentState = 'granted' | 'denied' | 'unset';

export function getConsent(): ConsentState {
  if (typeof window === 'undefined') return 'unset';
  try {
    const v = window.localStorage.getItem(CONSENT_KEY);
    if (v === 'granted' || v === 'denied') return v;
  } catch {
    // ignore
  }
  return 'unset';
}

export function setConsent(state: 'granted' | 'denied') {
  try {
    window.localStorage.setItem(CONSENT_KEY, state);
  } catch {
    // ignore
  }
  if (state === 'granted') {
    loadGtag();
  } else {
    // If the user later declines (e.g. clears site data and re-prompts),
    // we don't try to retroactively kill an already-loaded script. This
    // is rare and not worth the complexity.
  }
}

let gtagLoading = false;

export function loadGtag() {
  if (typeof window === 'undefined') return;
  if (window.gtag || gtagLoading) return;
  gtagLoading = true;

  window.dataLayer = window.dataLayer || [];
  // Google's recommended gtag stub. Cannot use a normal function with
  // rest args because gtag relies on the `arguments` object.
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GTAG_ID);

  const s = document.createElement('script');
  s.async = true;
  s.src = GTAG_SRC;
  document.head.appendChild(s);
}

// Auto-load on page load if consent was previously granted in this browser.
export function initAnalytics() {
  if (getConsent() === 'granted') loadGtag();
}

// Fire a Google Ads conversion. Safe to call regardless of consent state —
// if gtag never loaded, it's a no-op.
//
// `label` is the part after the slash in the conversion snippet's send_to,
// e.g. for "AW-18123061994/XAZtCKO-hKQcEOr138FD" pass "XAZtCKO-hKQcEOr138FD".
// `value` and `currency` are optional but recommended for lead conversions
// so Google's bidding can optimise toward higher-value lead sources.
export function trackConversion(
  label?: string,
  value?: number,
  currency: string = 'GBP'
) {
  if (typeof window === 'undefined' || !window.gtag) return;
  const payload: Record<string, unknown> = {
    send_to: label ? `${GTAG_ID}/${label}` : GTAG_ID,
  };
  if (value !== undefined) {
    payload.value = value;
    payload.currency = currency;
  }
  window.gtag('event', 'conversion', payload);
}

// Conversion label from Google Ads → Conversions → "Contact form submitted".
// Lives here so a future change is one edit, not a tour of the codebase.
export const CONTACT_FORM_CONVERSION = {
  label: 'XAZtCKO-hKQcEOr138FD',
  value: 1.0,
  currency: 'GBP',
} as const;
